import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const CASE_STUDIES = [
  {
    image: "https://images.unsplash.com/photo-1622015663319-e97e697503ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQ4Njk2NDl8MA&ixlib=rb-4.1.0&q=85",
    category: "Hospitality",
    title: "Serenity Villa, Goa",
    problem: "40% average occupancy, 90% reliance on Airbnb, zero direct bookings, inconsistent social presence.",
    strategy: "Full OTA optimization, Instagram reels campaign (3x/week), influencer partnerships, and a direct booking funnel via website.",
    result: "+62% occupancy, 35% direct bookings within 4 months, $18k saved in OTA commissions annually.",
    stat: "+62%",
    statLabel: "Occupancy",
  },
  {
    image: "https://images.pexels.com/photos/7676494/pexels-photo-7676494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    category: "Creator",
    title: "Alex Chen, Lifestyle Creator",
    problem: "120k followers, income limited to sporadic brand deals ($2-3k/month), no owned products, no email list.",
    strategy: "Digital product blueprint (online course), ConvertKit email ecosystem, 3-week launch campaign with affiliate partnerships.",
    result: "$12k first-month launch revenue, 4,200 email subscribers, recurring $8k/month from course sales.",
    stat: "$12k",
    statLabel: "Launch Revenue",
  },
  {
    image: "https://images.pexels.com/photos/7514814/pexels-photo-7514814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    category: "Hospitality",
    title: "Jade Retreat, Bali",
    problem: "New boutique hotel with no online presence, competing against established properties in a saturated market.",
    strategy: "Aggressive launch: Meta Ads + Google Ads, PR outreach to travel publications, Instagram growth strategy from zero.",
    result: "3x direct bookings in Q1, 15k Instagram followers in 90 days, featured in 3 major travel publications.",
    stat: "3x",
    statLabel: "Direct Bookings",
  },
  {
    image: "https://images.pexels.com/photos/36466231/pexels-photo-36466231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    category: "Creator",
    title: "Mia Torres, Fitness Creator",
    problem: "85k followers, $1.5k/month from brand deals, wanted to build a sustainable business beyond social media.",
    strategy: "Membership site launch, email nurture sequences, content repurposing strategy, community-driven growth.",
    result: "$10k launch, 300+ paying members at $29/month, 60% reduction in brand deal dependency.",
    stat: "$10k",
    statLabel: "Launch Revenue",
  },
];

export default function CaseStudies() {
  return (
    <div>
      {/* ========== HERO (DARK) ========== */}
      <section
        data-testid="casestudies-hero-section"
        className="relative bg-dark_base overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-6 font-body">
              Case Studies
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] tracking-tight font-extrabold text-cream mb-8">
              Real Results, <span className="font-accent italic font-normal text-mint">Real Growth</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg md:text-xl leading-relaxed text-cream/70 max-w-2xl font-body">
              Every project tells a story of transformation. Here's how we've helped properties and creators achieve measurable, lasting results.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== CASE STUDIES GRID (LIGHT) ========== */}
      <section className="bg-light_base -mt-12 rounded-t-[3rem] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16" data-testid="case-studies-grid">
            {CASE_STUDIES.map((cs, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`case-study-card-${i}`}
                className="group"
              >
                {/* Image */}
                <div className="relative rounded-2xl overflow-hidden mb-6 aspect-[16/10]">
                  <img
                    src={cs.image}
                    alt={cs.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark_base/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <span className="bg-mint text-dark_base text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      {cs.category}
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-2xl font-extrabold text-cream">{cs.stat}</span>
                      <span className="font-body text-xs text-cream/60">{cs.statLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-heading text-2xl font-semibold text-dark_base mb-4 flex items-center gap-2">
                  {cs.title}
                  <ArrowUpRight size={18} className="text-mint opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </h3>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-dark_base/40 mb-1 font-body">Problem</p>
                    <p className="font-body text-sm text-dark_base/70 leading-relaxed">{cs.problem}</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-dark_base/40 mb-1 font-body">Strategy</p>
                    <p className="font-body text-sm text-dark_base/70 leading-relaxed">{cs.strategy}</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-mint mb-1 font-body">Result</p>
                    <p className="font-body text-sm text-dark_base font-semibold leading-relaxed">{cs.result}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ========== CTA ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-dark_base rounded-3xl p-12 md:p-20 text-center"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Your Story is Next
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight font-bold text-cream mb-8">
              Ready to Become Our Next{" "}
              <span className="font-accent italic font-normal text-mint">Success Story?</span>
            </h2>
            <Link
              to="/contact"
              data-testid="casestudies-bottom-cta"
              className="inline-flex bg-mint text-dark_base font-bold py-4 px-10 rounded-full hover:bg-mint-hover transition-colors duration-300 font-body items-center gap-2"
            >
              Start a Conversation <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
